import React, { useEffect, useMemo, useRef, useState } from "react";

import CustomModal from "../Modal";

import CustomButton from "../Button";

import { VscLoading } from "react-icons/vsc";

import { PDFDocument } from "pdf-lib";

import JSZip from "jszip";

import { EMPLOYEE_CERTIFICATIONS } from "../../util/certificateGenerator";

import {
  captureCertificateElement,
  preloadCertificateAssets,
} from "../../util/certificateExport";

import {

  FARS_DFARS_2026_INFO,

  CompanyBundleType,

  getInitiativesForBundle,

  isCertInBundle,

} from "../../util/certificateBundles";

import CertificateCard from "../CertificateTemplate";
import "../../styles/certificateModal.css";
import "../../styles/employeeCertificate.css";



interface CertificateModalProps {

  open: boolean;

  onClose: () => void;

  employeeName: string;

  companyName: string;

}



const CertificateModal: React.FC<CertificateModalProps> = ({

  open,

  onClose,

  employeeName,

  companyName: companyNameProp,

}) => {

  const resolvedCompanyName =

    (companyNameProp && companyNameProp.trim()) || "OnMyWay";

  const [singleLoadingId, setSingleLoadingId] = useState<string | null>(null);

  const [allLoading, setAllLoading] = useState(false);

  const [bundleLoading, setBundleLoading] = useState(false);

  const [selectedBundle, setSelectedBundle] =

    useState<CompanyBundleType>("government_contractor");

  const [showBundleOnly, setShowBundleOnly] = useState(true);

  const captureRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (open) {
      preloadCertificateAssets();
    }
  }, [open]);

  const bundleInitiatives = useMemo(

    () => getInitiativesForBundle(selectedBundle),

    [selectedBundle]

  );

  const displayedInitiatives = useMemo(

    () =>

      showBundleOnly

        ? bundleInitiatives

        : EMPLOYEE_CERTIFICATIONS,

    [showBundleOnly, bundleInitiatives]

  );



  const getSafeName = (value: string) => value.trim().replace(/\s+/g, "_");



  const capturePreviewAsCanvas = async (initiativeId: string) => {

    const node = captureRefs.current[initiativeId];

    if (!node) {

      throw new Error(

        "Certificate preview not found. Turn off bundle filter or scroll to the certificate.",

      );

    }

    node.scrollIntoView({ block: "center", behavior: "auto" });

    await new Promise((r) => setTimeout(r, 200));

    return captureCertificateElement(node);

  };



  const canvasToPdfBlob = async (canvas: HTMLCanvasElement): Promise<Blob> => {

    const pngBlob: Blob = await new Promise((resolve, reject) => {

      canvas.toBlob((blob) => {

        if (blob) resolve(blob);

        else reject(new Error("Failed to create PNG blob from certificate"));

      }, "image/png");

    });

    const pngBytes = await pngBlob.arrayBuffer();

    const pdfDoc = await PDFDocument.create();

    const pngImage = await pdfDoc.embedPng(pngBytes);

    const page = pdfDoc.addPage([canvas.width, canvas.height]);

    page.drawImage(pngImage, {

      x: 0,

      y: 0,

      width: canvas.width,

      height: canvas.height,

    });

    const pdfBytes = await pdfDoc.save();

    return new Blob([pdfBytes], { type: "application/pdf" });

  };



  const downloadBlob = (blob: Blob, fileName: string) => {

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.download = fileName;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(url);

  };



  const downloadInitiativesAsZip = async (

    initiatives: { id: string }[],

    zipFileName: string

  ) => {

    const zip = new JSZip();

    for (const initiative of initiatives) {

      const canvas = await capturePreviewAsCanvas(initiative.id);

      const pdfBlob = await canvasToPdfBlob(canvas);

      zip.file(`${getSafeName(employeeName)}_${initiative.id}.pdf`, pdfBlob);

      await new Promise((r) => setTimeout(r, 150));

    }

    const zipBlob = await zip.generateAsync({ type: "blob" });

    downloadBlob(zipBlob, zipFileName);

  };



  const handleDownloadSingle = async (initiativeId: string) => {

    setSingleLoadingId(initiativeId);

    try {

      const initiative = EMPLOYEE_CERTIFICATIONS.find((i) => i.id === initiativeId);

      if (!initiative) return;



      const canvas = await capturePreviewAsCanvas(initiativeId);

      const pdfBlob = await canvasToPdfBlob(canvas);

      const fileName = `${getSafeName(employeeName)}_${initiative.id}.pdf`;

      downloadBlob(pdfBlob, fileName);

    } catch (error) {

      console.error("Error downloading certificate:", error);

    } finally {

      setSingleLoadingId(null);

    }

  };



  const handleDownloadAll = async () => {

    setAllLoading(true);

    try {

      const list = showBundleOnly

        ? displayedInitiatives

        : EMPLOYEE_CERTIFICATIONS;

      await downloadInitiativesAsZip(

        list,

        `${getSafeName(employeeName)}_certificates_all.zip`

      );

    } catch (error) {

      console.error("Error downloading certificates:", error);

    } finally {

      setAllLoading(false);

    }

  };



  const handleDownloadBundle = async () => {

    setBundleLoading(true);

    try {

      await downloadInitiativesAsZip(

        bundleInitiatives,

        `${getSafeName(employeeName)}_${selectedBundle}_bundle.zip`

      );

    } catch (error) {

      console.error("Error downloading certificate bundle:", error);

    } finally {

      setBundleLoading(false);

    }

  };



  const isBusy = allLoading || bundleLoading || !!singleLoadingId;

  const renderEmployeeCertCard = (initiative: (typeof EMPLOYEE_CERTIFICATIONS)[0]) => (
    <div
      key={initiative.id}
      className="certificate-modal__cert-card bg-primary rounded-lg p-3 flex flex-col gap-2 hover:bg-gray/50 transition-colors"
    >
      <div
        className="relative w-full certificate-capture"
        ref={(el) => {
          captureRefs.current[initiative.id] = el;
        }}
      >
        <CertificateCard
          cert={initiative}
          recipientName={employeeName}
          showBrandName={false}
          showIcon={false}
          elementId={`certificate-${initiative.id}`}
          className="certificate--modal certificate--square"
        />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-primaryText text-sm font-medium">{initiative.title}</p>
        <p className="text-grayLight text-xs line-clamp-2">{initiative.description}</p>
      </div>
      <CustomButton
        title={
          singleLoadingId === initiative.id ? (
            <VscLoading className="animate-spin" />
          ) : (
            "Download PDF"
          )
        }
        onClick={() => handleDownloadSingle(initiative.id)}
        disabled={isBusy || singleLoadingId === initiative.id}
        className="!py-1 !text-sm"
      />
    </div>
  );

  return (

    <CustomModal

      open={open}

      onClose={onClose}

      label="OnMyWay Safety & Compliance Certificates"

      maxWidth={1280}

      contentClassName="certificate-modal-shell"

    >

      <div className="certificate-modal flex flex-col gap-4 mt-2 sm:mt-4">

        <div className="certificate-modal__header">

          <div className="min-w-0">

            <p className="text-primaryText text-base sm:text-lg font-semibold truncate">

              {employeeName}

            </p>

            <p className="text-grayLight text-xs sm:text-sm truncate">{resolvedCompanyName}</p>

          </div>

          <div className="certificate-modal__actions">

            <CustomButton

              title={

                bundleLoading ? (

                  <VscLoading className="animate-spin text-xl" />

                ) : (

                  `Download Bundle (${bundleInitiatives.length})`

                )

              }

              onClick={handleDownloadBundle}

              disabled={isBusy}

              className="!py-2"

            />

            <CustomButton

              title={

                allLoading ? (

                  <VscLoading className="animate-spin text-xl" />

                ) : (

                  `Download All (${

                    showBundleOnly

                      ? displayedInitiatives.length

                      : EMPLOYEE_CERTIFICATIONS.length

                  })`

                )

              }

              onClick={handleDownloadAll}

              disabled={isBusy}

              className="!py-2"

            />

          </div>

        </div>



        <div className="certificate-modal__panel bg-primary rounded-lg flex flex-col gap-3 border border-gray/30">

          {/* Recommended certificate bundle — bundle tabs & program copy hidden per client request
          <p className="text-primaryText text-sm font-semibold">
            Recommended certificate bundle
          </p>
          <div className="flex flex-wrap gap-2">
            {CERTIFICATE_BUNDLES.map((bundle) => (
              <button
                key={bundle.id}
                type="button"
                onClick={() => setSelectedBundle(bundle.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedBundle === bundle.id
                    ? "bg-blue-500 text-white"
                    : "bg-gray/40 text-primaryText hover:bg-gray/60"
                }`}
              >
                {bundle.shortLabel}
              </button>
            ))}
          </div>
          {activeBundle && (
            <>
              <p className="text-primaryText text-sm font-semibold">
                {activeBundle.certificationProgramName}
              </p>
              <p className="text-grayLight text-xs">{activeBundle.label}</p>
              <p className="text-grayLight text-xs leading-relaxed">
                {activeBundle.description}
              </p>
              <p className="text-grayLight text-xs">
                {getEmployeeBundleSummaryText(selectedBundle)}
              </p>
              <p className="text-grayLight text-[10px]">
                PDF download matches certificate preview (WYSIWYG). Company
                certifications: sidebar → Company Certifications
              </p>
            </>
          )}
          */}

          {selectedBundle === "government_contractor" && (

            <div className="rounded-lg bg-gray/20 p-3 flex flex-col gap-1">

              <p className="text-primaryText text-xs font-semibold">

                {FARS_DFARS_2026_INFO.title} — {FARS_DFARS_2026_INFO.effectiveDate}

              </p>

              <p className="text-grayLight text-xs leading-relaxed">

                {FARS_DFARS_2026_INFO.summary}

              </p>

              <p className="text-grayLight text-[10px] italic">

                {FARS_DFARS_2026_INFO.disclaimer}

              </p>

            </div>

          )}

          <label className="flex items-center gap-2 text-primaryText text-xs cursor-pointer">

            <input

              type="checkbox"

              checked={showBundleOnly}

              onChange={(e) => setShowBundleOnly(e.target.checked)}

              className="rounded"

            />

            Show only certificates in selected bundle

          </label>

        </div>



        {showBundleOnly ? (
          <div className="certificate-modal__scroll certificate-modal__cert-grid">
            {bundleInitiatives.map((initiative) => renderEmployeeCertCard(initiative))}
          </div>
        ) : (
          <div className="certificate-modal__scroll certificate-modal__cert-grid">
            {displayedInitiatives.map((initiative) => {
              const inBundle = isCertInBundle(initiative.id, selectedBundle);

              return (
                <div key={initiative.id} className="relative">
                  {inBundle && (
                    <span className="absolute top-2 right-2 z-10 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-500/90 text-white">
                      In bundle
                    </span>
                  )}
                  {renderEmployeeCertCard(initiative)}
                </div>
              );
            })}
          </div>
        )}



        <div className="text-center text-grayLight text-xs pt-2 border-t border-gray/30">

          OnMyWay Safety Platform • Training Completion & Readiness Certificates

        </div>

      </div>

    </CustomModal>

  );

};



export default CertificateModal;


